import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import RondleidingForm from "./RondleidingForm";

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isWhatWeOfferOpen, setIsWhatWeOfferOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [openForm, setOpenForm] = React.useState<null | "rondleiding">(null);

  const { t } = useLanguage();
  const location = useLocation();

  // About Us dropdown items
  const aboutItems = [
    { path: "/about", label: t("nav.insideus") },
    { path: "/vision", label: t("nav.vision") },
  ];

  // What We Offer dropdown items
  const whatWeOfferItems = [
    { path: "/services", label: t("nav.services") },
    { path: "/transportation-policy", label: t("nav.transportationPolicy") },
    { path: "/nutrition", label: t("nav.nutrition") },
    { path: "/ipad-system", label: t("nav.ipadSystem") },
    { path: "/safety-hygiene", label: t("nav.healthSafety") },
  ];

  // More dropdown items
  const moreItems = {
    column1: {
      title: t("nav.ourDay"),
      items: [
        { path: "/daily-routine", label: t("nav.dailyRoutine") },
        { path: "/emotional-safety", label: t("nav.emotionalSafety") },
        { path: "/activities", label: t("nav.activities") },
      ],
    },
    column2: {
      title: t("nav.learningGrowth"),
      items: [
        //{ path: "/horizontal-groups", label: t("nav.horizontalGroups") },
        { path: "/educational-staff", label: t("nav.educationalStaff") },
        { path: "/receiving-policy", label: t("nav.receivingPolicy") },
      ],
    },
    column3: {
      title: t("nav.familyConnection"),
      items: [
        { path: "/parent-collaboration", label: t("nav.parentCollaboration") },
        { path: "/faq", label: t("nav.faq") },
      ],
    },
    // column4: {
    //   title: t("nav.healthSafety"),
    //   items: [
    //     {
    //       path: "/opening-closing-checklist",
    //       label: t("nav.openingClosingChecklist"),
    //     },
    //   ],
    // },
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50">
      <div className="max-w-8xl px-8 mx-auto sm:px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo and Brand Name */}
          <Link
            to="/"
            className="flex items-center space-x-3 min-w-0 flex-shrink-0 lg:space-x-4"
          >
            <div className="flex items-center space-x-3 min-w-0 flex-shrink-0 lg:space-x-4">
              <div className="flex-shrink-0">
                <Logo size="medium" />
              </div>
              <div className="flex flex-col min-w-0 max-[380px]:hidden">
                <div className="font-child font-bold text-lg lg:text-xl text-dark-2 truncate">
                  Mini Avonturiers
                </div>
                <div className="text-xs lg:text-sm text-dark-3 truncate">
                  {t("brand.tagline")}
                </div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center justify-center flex-1 px-2 sm:px-4">
            <div className="flex items-center space-x-2 ">
              {/* Home */}
              <Link
                to="/"
                className={`font-child font-medium text-xs sm:text-sm md:text-base transition-colors duration-200 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap ${
                  isActive("/")
                    ? "bg-[#F9DCAC] text-gray-900"
                    : "text-gray-700 hover:text-gray-950"
                }`}
              >
                {t("nav.home")}
              </Link>

              {/* About Us Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsAboutOpen(!isAboutOpen)}
                  onBlur={() => setTimeout(() => setIsAboutOpen(false), 150)}
                  className={`font-child font-medium text-sm lg:text-base transition-colors duration-200 px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap ${
                    aboutItems.some((item) => isActive(item.path))
                      ? "bg-[#F9DCAC] text-gray-900"
                      : "text-gray-700 hover:text-gray-950"
                  }`}
                >
                  <span className="truncate">{t("nav.about")}</span>
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isAboutOpen && (
                  <div className="absolute left-0 mt-2 w-72 bg-[#F9DCAC] rounded-xl shadow-lg py-2 z-50 ">
                    {aboutItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-3 font-child text-sm lg:text-base transition-colors duration-200 whitespace-nowrap ${
                          isActive(item.path)
                            ? "text-secondary-orange bg-lighter"
                            : "text-dark-2 hover:text-secondary-orange hover:bg-lighter"
                        }`}
                        onClick={() => setIsAboutOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* What We Offer Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsWhatWeOfferOpen(!isWhatWeOfferOpen)}
                  onBlur={() =>
                    setTimeout(() => setIsWhatWeOfferOpen(false), 150)
                  }
                  className={`font-child font-medium text-sm lg:text-base transition-colors duration-200 px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap ${
                    whatWeOfferItems.some((item) => isActive(item.path))
                      ? "bg-[#F9DCAC] text-gray-900"
                      : "text-gray-700 hover:text-gray-950"
                  }`}
                >
                  <span className="truncate">{t("nav.whatWeOffer")}</span>
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isWhatWeOfferOpen && (
                  <div className="absolute left-0 mt-2 w-80 bg-[#F9DCAC] rounded-xl shadow-lg py-2 z-50 ">
                    {whatWeOfferItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-3 font-child text-sm lg:text-base transition-colors duration-200 whitespace-nowrap ${
                          isActive(item.path)
                            ? "text-secondary-orange bg-lighter"
                            : "text-dark-2 hover:text-secondary-orange hover:bg-lighter"
                        }`}
                        onClick={() => setIsWhatWeOfferOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* More Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  onBlur={() => setTimeout(() => setIsMoreOpen(false), 150)}
                  className={`font-child font-medium text-sm lg:text-base transition-colors duration-200 px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap
                    min-w-[80px] justify-center ${
                      Object.values(moreItems).some((column) =>
                        column.items.some((item) => isActive(item.path))
                      )
                        ? "bg-[#F9DCAC] text-gray-900"
                        : "text-gray-700 hover:text-gray-950"
                    }`}
                >
                  <span className="truncate">{t("nav.more")}</span>
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {/* More Dropdown */}
                {isMoreOpen && (
                  <div className="absolute right-0 mt-2 min-w-[700px] bg-white rounded-xl shadow-lg py-6 z-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 px-6 md:px-8">
                      {Object.entries(moreItems).map(([columnKey, column]) => (
                        <div key={columnKey} className="space-y-4">
                          <h3 className="font-child font-bold text-sm md:text-base text-neutral-800 uppercase tracking-wide border-b border-[#492A19] pb-2">
                            {column.title}
                          </h3>
                          <div className="space-y-3">
                            {column.items.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`block font-child text-xs sm:text-sm md:text-base transition-colors duration-200 py-2 px-3 rounded-lg whitespace-nowrap ${
                                  isActive(item.path)
                                    ? "text-secondary-orange bg-lighter font-medium"
                                    : "text-dark-2 hover:text-secondary-orange hover:bg-lighter"
                                }`}
                                onClick={() => setIsMoreOpen(false)}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Us */}
              <Link
                to="/contact"
                className={`font-child font-medium text-sm lg:text-base transition-colors duration-200 px-4 py-2 rounded-lg whitespace-nowrap ${
                  isActive("/contact")
                    ? "bg-[#F9DCAC] text-gray-900"
                    : "text-gray-700 hover:text-gray-950"
                }`}
              >
                {t("common.contactUs")}
              </Link>
            </div>
          </nav>

          {/* Right Side - Language Switcher and Register Dropdown */}
          <div className="flex items-center space-x-6 flex-shrink-0">
            {/* Language Switcher */}
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>

            {/* Register Dropdown */}
            {
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsRegisterOpen(!isRegisterOpen)}
                  onBlur={() => setTimeout(() => setIsRegisterOpen(false), 150)}
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#FBEACD] text-[#492A19] font-child font-bold text-sm lg:text-base rounded-full border-2 transition-all duration-300 hover:bg-[#492A19] hover:text-[#F9DCAC]"
                  style={{ borderColor: "#492A19" }}
                >
                  {t("auth.register")}
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isRegisterOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#FBEACD] rounded-xl shadow-lg py-2 z-50 ">
                    <Link
                      to="/register"
                      className="block px-4 py-3 font-child text-sm lg:text-base transition-colors duration-200 whitespace-nowrap text-gray-700 hover:text-gray-950 hover:bg-[#F9DCAC]"
                      onClick={() => setIsRegisterOpen(false)}
                    >
                      {t("home.registerChild")}
                    </Link>
                    <button
                      className="block px-4 py-3 mx-auto w-full font-child text-sm lg:text-base transition-colors duration-200 whitespace-nowrap text-gray-700 hover:text-gray-950 hover:bg-[#F9DCAC]"
                      onClick={() => setOpenForm("rondleiding")}
                    >
                      {t("home.registerTour")}
                    </button>
                  </div>
                )}
              </div>
            }

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 rounded-lg text-neutral-700 hover:text-[#492A19] hover:bg-neutral-100 transition-colors duration-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="xl:hidden min-h-[95vh] border-t border-neutral-200 inset-0 py-8 my-20 fixed w-full overflow-y-auto bg-white">
            <nav className="space-y-4">
              {/* Home */}
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`block font-child font-medium text-lg transition-colors duration-200 ${
                  isActive("/")
                    ? "text-blue-600"
                    : "text-neutral-700 hover:text-blue-600"
                }`}
              >
                {t("nav.home")}
              </Link>

              {/* About Us */}
              <div className="space-y-2">
                <div className="font-child font-medium text-lg text-neutral-800">
                  {t("nav.about")}
                </div>
                <div className="space-y-2">
                  {aboutItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`block font-child text-base transition-colors duration-200 ${
                        isActive(item.path)
                          ? "text-peach-600"
                          : "text-neutral-700 hover:text-peach-600"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* What We Offer */}
              <div className="space-y-2">
                <div className="font-child font-medium text-lg text-neutral-800">
                  {t("nav.whatWeOffer")}
                </div>
                <div className="space-y-2">
                  {whatWeOfferItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`block font-child text-base transition-colors duration-200 ${
                        isActive(item.path)
                          ? "text-mint-600"
                          : "text-neutral-700 hover:text-mint-600"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* More */}
              <div className="space-y-2">
                <div className="font-child font-medium text-lg text-neutral-800">
                  {t("nav.more")}
                </div>
                <div className="space-y-4">
                  {Object.entries(moreItems).map(([columnKey, column]) => (
                    <div key={columnKey} className="space-y-2">
                      <h4 className="font-child font-bold text-sm text-neutral-700 uppercase tracking-wide">
                        {column.title}
                      </h4>
                      <div className="space-y-2">
                        {column.items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block font-child text-base transition-colors duration-200 ${
                              isActive(item.path)
                                ? "text-purple-600"
                                : "text-neutral-700 hover:text-purple-600"
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Us */}
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className={`block font-child font-medium text-lg transition-colors duration-200 ${
                  isActive("/contact")
                    ? "text-blue-600"
                    : "text-neutral-700 hover:text-blue-600"
                }`}
              >
                {t("common.contactUs")}
              </Link>

              <div className="sm:hidden flex justify-center">
                <LanguageSwitcher />
              </div>

              {
                <div className="py-12 md:hidden border-t border-neutral-200 space-y-4">
                  <Link
                    to="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-flex items-center justify-center w-[60%] px-6 py-3 bg-[#FBEACD] text-[#492A19] font-child font-bold rounded-full border-2 transition-all duration-300 hover:bg-[#492A19] hover:text-[#F9DCAC]"
                    style={{ borderColor: "#492A19" }}
                  >
                    {t("home.registerChild")}
                  </Link>
                  <button
                    onClick={() => setOpenForm("rondleiding")}
                    className="inline-flex items-center justify-center w-[60%] px-6 py-3 bg-[#FBEACD] text-[#492A19] font-child font-bold rounded-full border-2 transition-all duration-300 hover:bg-[#492A19] hover:text-[#F9DCAC]"
                    style={{ borderColor: "#492A19" }}
                  >
                    {t("home.registerTour")}
                  </button>
                </div>
              }
            </nav>
          </div>
        )}
      </div>
      {/* Forms Modals */}
      {openForm === "rondleiding" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
          <div className="bg-[#FBEACD] rounded-2xl p-4 sm:p-6 md:p-8 w-full max-w-sm sm:max-w-md md:max-w-lg relative">
            <button
              onClick={() => setOpenForm(null)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-xl sm:text-2xl"
            >
              &times;
            </button>
            <RondleidingForm />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
